import { createSelector } from 'reselect'
import { DateTime } from 'luxon'

import {
  filterTransactionsByType,
  getTransactionsInYearRange,
  getUsersTransactions,
} from '@/features/transactions/transactions.selectors'
import {
  addCurrencyArray,
  centsToCurrency,
  dollarsToCents,
  dollarsToCurrency,
} from '@/utils/currencyHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { getProfitGoal } from '@/features/goal-setting/helpers'

// Multiply current amount by this to get projected yearly amount
const getYtdRatio = () => {
  const startOfYear = DateTime.now().startOf('year')
  const dayDiff = DateTime.now().diff(startOfYear, 'days').days
  const totalDays = startOfYear.endOf('year').diff(startOfYear, 'days').days

  return totalDays / dayDiff
}

export const selectYtdIncome = createSelector(
  getUsersTransactions,
  (userTransactions) => {
    const currentYear = DateTime.now().year

    const ytdIncomeTransactions = getTransactionsInYearRange(
      filterTransactionsByType(userTransactions, 'businessIncome'),
      currentYear
    )

    return addCurrencyArray(
      ytdIncomeTransactions.map((t) => centsToCurrency(t.amountInCents))
    )
  }
)

export const selectLastYearIncome = createSelector(
  getUsersTransactions,
  (userTransactions) => {
    const lastYear = DateTime.now().year - 1

    const lastYearIncomeTransactions = getTransactionsInYearRange(
      filterTransactionsByType(userTransactions, 'businessIncome'),
      lastYear
    )

    return addCurrencyArray(
      lastYearIncomeTransactions.map((t) => centsToCurrency(t.amountInCents))
    )
  }
)

export const selectProjectedIncome = createSelector(
  selectYtdIncome,
  (ytdIncome) => dollarsToCurrency(ytdIncome).multiply(getYtdRatio())
)

export const selectYtdExpenses = createSelector(
  getUsersTransactions,
  (userTransactions) => {
    const currentYear = DateTime.now().year

    const ytdExpensesTransactions = getTransactionsInYearRange(
      filterTransactionsByType(userTransactions, 'categorizedExpenses'),
      currentYear
    )

    return dollarsToCurrency(
      addCurrencyArray(
        ytdExpensesTransactions.map((t) => centsToCurrency(t.amountInCents))
      )
    ).value
  }
)

export const selectLastYearExpenses = createSelector(
  getUsersTransactions,
  (userTransactions) => {
    const lastYear = DateTime.now().year - 1

    const lastYearExpensesTransactions = getTransactionsInYearRange(
      filterTransactionsByType(userTransactions, 'categorizedExpenses'),
      lastYear
    )

    return dollarsToCurrency(
      addCurrencyArray(
        lastYearExpensesTransactions.map((t) =>
          centsToCurrency(t.amountInCents)
        )
      )
    ).value
  }
)

export const selectYtdExpenseRatio = createSelector(
  selectYtdExpenses,
  selectYtdIncome,
  (ytdExpenses, ytdIncome) => {
    // Handles edge case where percentage can be less than 0 if expenses positive or income negative
    const percentage = (ytdExpenses / ytdIncome) * -100
    return percentage > 0 ? percentage.toFixed(2) : '-'
  }
)

export const selectLastYearExpenseRatio = createSelector(
  selectLastYearExpenses,
  selectLastYearIncome,
  (lastYearExpenses, lastYearIncome) =>
    lastYearIncome
      ? ((lastYearExpenses / lastYearIncome) * -100).toFixed(2)
      : '-'
)

export const selectExpenseRatioGoalDifference = createSelector(
  selectYtdExpenseRatio,
  getFinancialProfile,
  (ytdExpenseRatio, fp) => {
    if (!fp?.annualExpenseRatioGoal) {
      return 0
    }

    return (Number(ytdExpenseRatio) / 100 - fp.annualExpenseRatioGoal) * 100
  }
)

export const selectProjectedExpenses = createSelector(
  selectYtdExpenses,
  (ytdExpenses) =>
    ytdExpenses < 0
      ? dollarsToCurrency(ytdExpenses).multiply(getYtdRatio())
      : dollarsToCurrency(0)
)

export const selectProjectedProfit = createSelector(
  selectProjectedIncome,
  selectProjectedExpenses,
  (projectedIncome, projectedExpenses) =>
    dollarsToCurrency(projectedIncome).add(projectedExpenses)
)

export const selectLastYearProfit = createSelector(
  selectLastYearIncome,
  selectLastYearExpenses,
  (lastYearIncome, lastYearExpenses) =>
    dollarsToCurrency(lastYearIncome).add(lastYearExpenses)
)

export const selectIncomeGoalPercentage = createSelector(
  selectYtdIncome,
  getFinancialProfile,
  (ytdIncome, fp) =>
    fp?.annualIncomeGoalInCents
      ? Number(
          (
            (dollarsToCents(ytdIncome) / fp.annualIncomeGoalInCents) *
            100
          ).toFixed(2)
        )
      : 0
)

export const selectYtdProfit = createSelector(
  selectYtdIncome,
  selectYtdExpenses,
  (ytdIncome, ytdExpenses) => dollarsToCurrency(ytdIncome).add(ytdExpenses)
)

export const selectProfitGoal = createSelector(getFinancialProfile, (fp) =>
  getProfitGoal(fp?.annualIncomeGoalInCents, fp?.annualExpenseRatioGoal)
)

export const selectProfitGoalPercentage = createSelector(
  selectYtdProfit,
  selectProfitGoal,
  (ytdProfit, profitGoal) => {
    if (!profitGoal) {
      return 0
    }

    return Number(((ytdProfit.value / profitGoal) * 100).toFixed(2))
  }
)
